//@flow
import React, {createContext, useContext} from 'react';
import {useRef} from 'react';
import {useState} from 'react';
import {useReducer} from 'react';
import type {BulkEditProductVariant, VariantOption} from '../../../entities';
import {useDebounceEffect} from '../../../utils/react';
import type {ProductVariantsRequest} from '../../products/api';
import {useEditProduct} from '../utils/editProduct';

type VariantRequest = {
  imageId?: string,
  options?: {[key: string]: string},
  price?: string,
  offerPrice?: string,
  enabledBulkPrice?: boolean,
  stock?: number,
  sku?: string,
  enabled?: boolean,
  id: string,
};

type ProductVariantContextState = {
  setSku: (variantId: string, sku: string) => void,
  setPrice: (variantId: string, price: string) => void,
  setTaxedPrice: (variantId: string, price: string) => void,
  setOfferPrice: (variantId: string, offerPrice: string) => void,
  setTaxedOfferPrice: (variantId: string, offerPrice: string) => void,
  setStock: (variantId: string, stock: number) => void,
  setEnabled: (variantId: string, enabled: boolean) => void,
  setEnabledBulkPrice: (variantId: string, enabledBulkPrice: boolean) => void,
  setImageId: (variantId: string, imageId: string) => void,
  variantList: BulkEditProductVariant[],

  enabledMixAndMatch: boolean,
  setEnabledMixAndMatch: (enabledMixAndMatch: boolean) => void,
};

const ProductVariantContext = createContext<ProductVariantContextState | null>(
  null
);

type Props = {
  children: React$Node,
  variants: BulkEditProductVariant[],
  options: VariantOption[],
  productId: string,
  enabledMixAndMatch?: boolean,
};

type Action = {
  type:
    | 'SET_PRICE'
    | 'SET_TAXED_PRICE'
    | 'SET_OFFER_PRICE'
    | 'SET_TAXED_OFFER_PRICE'
    | 'SET_STOCK'
    | 'SET_SKU'
    | 'SET_ENABLED'
    | 'SET_ENABLED_BULK_PRICE'
    | 'SET_IMAGE_ID',

  payload: VariantRequest,
};

function reducer(
  state: BulkEditProductVariant[],
  action: Action
): BulkEditProductVariant[] {
  const {payload, type} = action;

  if (type === 'SET_PRICE') {
    return state.map(variant => {
      if (variant.id === payload.id && payload.price) {
        return {
          ...variant,
          price: payload.price,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_TAXED_PRICE') {
    return state.map(variant => {
      if (variant.id === payload.id && payload.price) {
        return {
          ...variant,
          taxedPrice: payload.price,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_OFFER_PRICE') {
    return state.map(variant => {
      if (variant.id === payload.id) {
        return {
          ...variant,
          offerPrice: payload.offerPrice,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_TAXED_OFFER_PRICE') {
    return state.map(variant => {
      if (variant.id === payload.id && payload.offerPrice) {
        return {
          ...variant,
          taxedOfferPrice: payload.offerPrice,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_STOCK') {
    return state.map(variant => {
      if (variant.id === payload.id) {
        return {
          ...variant,
          stock: payload.stock,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_SKU') {
    return state.map(variant => {
      if (variant.id === payload.id) {
        return {
          ...variant,
          sku: payload.sku,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_ENABLED') {
    return state.map(variant => {
      if (variant.id === payload.id) {
        return {
          ...variant,
          enabled: payload.enabled,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_ENABLED_BULK_PRICE') {
    return state.map(variant => {
      if (variant.id === payload.id) {
        return {
          ...variant,
          enabledBulkPrice: !!payload.enabledBulkPrice,
        };
      }

      return variant;
    });
  }

  if (type === 'SET_IMAGE_ID') {
    return state.map(variant => {
      if (variant.id === payload.id && payload.imageId) {
        return {
          ...variant,
          imageId: payload.imageId,
        };
      }

      return variant;
    });
  }

  return state;
}

export function ProductVariantRequestProvider({
  children,
  variants,
  options,
  productId,
  enabledMixAndMatch: enabledMixAndMatchProp,
}: Props) {
  const {editProductInBulkEdit} = useEditProduct(productId);

  const [enabledMixAndMatch, setEnabledMixAndMatch] = useState(
    enabledMixAndMatchProp
  );
  const [state, dispatch] = useReducer(reducer, variants);

  const firstRender = useRef(true);
  const mixAndMatchFLag = useRef(true);

  const setPrice = (variantId: string, price: string) => {
    dispatch({
      type: 'SET_PRICE',
      payload: {
        price,
        id: variantId,
      },
    });
  };

  const setTaxedPrice = (variantId: string, price: string) => {
    dispatch({
      type: 'SET_TAXED_PRICE',
      payload: {
        price,
        id: variantId,
      },
    });
  };

  const setOfferPrice = (variantId: string, offerPrice: string) => {
    dispatch({
      type: 'SET_OFFER_PRICE',
      payload: {
        offerPrice,
        id: variantId,
      },
    });
  };

  const setTaxedOfferPrice = (variantId: string, offerPrice: string) => {
    dispatch({
      type: 'SET_TAXED_OFFER_PRICE',
      payload: {
        offerPrice,
        id: variantId,
      },
    });
  };

  const setStock = (variantId: string, stock: number) => {
    dispatch({
      type: 'SET_STOCK',
      payload: {
        stock,
        id: variantId,
      },
    });
  };

  const setSku = (variantId: string, sku: string) => {
    dispatch({
      type: 'SET_SKU',
      payload: {
        sku,
        id: variantId,
      },
    });
  };

  const setEnabled = (variantId: string, enabled: boolean) => {
    dispatch({
      type: 'SET_ENABLED',
      payload: {
        enabled,
        id: variantId,
      },
    });
  };

  const setEnabledBulkPrice = (
    variantId: string,
    enabledBulkPrice: boolean
  ) => {
    dispatch({
      type: 'SET_ENABLED_BULK_PRICE',
      payload: {
        enabledBulkPrice,
        id: variantId,
      },
    });
  };

  const setImageId = (variantId: string, imageId: string) => {
    dispatch({
      type: 'SET_IMAGE_ID',
      payload: {
        imageId,
        id: variantId,
      },
    });
  };

  useDebounceEffect(
    () => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }

      const variantRequest: ProductVariantsRequest = {
        variants: state.map(variant => {
          return {
            id: variant.id,
            imageId: variant.imageId ? variant.imageId : undefined,
            combination: variant.combinations,
            price: String(variant.taxedPrice),
            // offerPrice: !isNaN(parseInt(variant.offerPrice))
            //   ? variant.offerPrice
            //   : undefined,
            offerPrice: !isNaN(parseInt(variant.taxedOfferPrice))
              ? String(variant.taxedOfferPrice)
              : undefined,
            enabledBulkPrice: !!variant.enabledBulkPrice,
            stock: !isNaN(parseInt(variant.stock))
              ? parseInt(variant.stock)
              : undefined,
            sku: variant.sku ?? undefined,
            enabled: variant.enabled,
          };
        }),
        options: options.map(option => {
          return {
            id: option.id,
            values: option.values.map(value => value.id),
          };
        }),
      };

      console.log({variantRequest});

      editProductInBulkEdit({
        variantData: variantRequest,
        type: 'VARIANT',
      });
    },
    750,
    [state, options]
  );

  useDebounceEffect(
    () => {
      if (mixAndMatchFLag.current) {
        mixAndMatchFLag.current = false;
        return;
      }

      editProductInBulkEdit({
        bulkPriceSetting: {
          enabledMixAndMatch,
        },
        type: 'VARIANT',
      });
    },
    750,
    [enabledMixAndMatch]
  );

  return (
    <ProductVariantContext.Provider
      value={{
        setPrice,
        setTaxedPrice,
        setOfferPrice,
        setTaxedOfferPrice,
        setStock,
        setSku,
        setEnabled,
        setEnabledBulkPrice,
        variantList: state,
        enabledMixAndMatch: !!enabledMixAndMatch,
        setEnabledMixAndMatch,
        setImageId,
      }}
    >
      {children}
    </ProductVariantContext.Provider>
  );
}

export function useProductVariantRequestContext(): ProductVariantContextState {
  const requestActions = useContext(ProductVariantContext);

  if (!requestActions) {
    throw new Error(
      'useProductVariantRequestContext must be used within a ProductVariantRequestProvider'
    );
  }

  return requestActions;
}

export function useProductVariantRequestState(): BulkEditProductVariant[] {
  const requestActions = useContext(ProductVariantContext);

  if (!requestActions) {
    throw new Error(
      'useProductVariantRequestState must be used within a ProductVariantRequestProvider'
    );
  }

  return requestActions.variantList;
}

export function useVariantMixAndMatch(): [boolean, (enabled: boolean) => void] {
  const requestActions = useContext(ProductVariantContext);

  if (!requestActions) {
    throw new Error(
      'useMixAndMatchEnabled must be used within a ProductVariantRequestProvider'
    );
  }
  const {enabledMixAndMatch, setEnabledMixAndMatch} = requestActions;

  return [enabledMixAndMatch, setEnabledMixAndMatch];
}
